import { useState } from 'preact/hooks';
import { t } from '../i18n/index.js';
import { formatDuration, useServerNow, type ArenaState } from '../arena/useArena.js';
import type { Realtime } from '../realtime.js';
import type { ArenaResult, ChallengeView } from '../../shared/protocol.js';

/** Incoming challenge cards and challenge notices, shown above every view. */
export function ChallengeLayer(props: { realtime: Realtime; arena: ArenaState; userId: number; showPmNotices: boolean }) {
  const { arena, realtime } = props;
  const serverNow = useServerNow(realtime.clockOffset, 250);
  const [error, setError] = useState<string | null>(null);
  const incoming = arena.me?.incoming ?? [];

  const respond = (c: ChallengeView, accept: boolean) => {
    setError(null);
    realtime.socket?.emit('challenge:respond', { id: c.id, accept }, (r: ArenaResult) => {
      if (!r.ok) setError(t.arena.errors[r.error]);
    });
  };

  const noticeText = (c: ChallengeView, reason: string) => {
    const iAmChallenger = c.from.id === props.userId;
    const other = iAmChallenger ? c.to.displayName : c.from.displayName;
    if (reason === 'declined') return t.arena.notices.declined(other);
    if (reason === 'expired') return iAmChallenger ? t.arena.notices.expiredOut(other) : t.arena.notices.expiredIn(other);
    if (reason === 'accepted') return t.arena.notices.accepted(other);
    return t.arena.notices.cancelled(other);
  };

  const pmNotices = props.showPmNotices ? arena.pmNotices : [];
  if (incoming.length === 0 && arena.notices.length === 0 && pmNotices.length === 0 && !error) return null;

  return (
    <div class="toast-layer" aria-live="polite">
      {incoming.map((c) => {
        const left = c.expiresAt - serverNow;
        return (
          <div class="toast challenge-card" role="alertdialog" aria-label={t.arena.incomingTitle}>
            <div class="toast-title">{t.arena.incomingTitle}</div>
            <div>
              {t.arena.incomingText(
                c.from.displayName,
                c.options?.clock ? `${t.games[c.gameType].name} (${t.chess.clockLabel(c.options.clock.baseMin, c.options.clock.incSec)})` : t.games[c.gameType].name,
              )}
            </div>
            <div class="bar">
              <div class="bar-fill" style={{ width: `${Math.max(0, Math.min(100, (left / 60000) * 100))}%` }} />
            </div>
            <div class="toast-actions">
              <span class="countdown">{formatDuration(left)}</span>
              <button class="btn btn-small" onClick={() => respond(c, false)}>
                {t.arena.decline}
              </button>
              <button class="btn btn-primary btn-small" onClick={() => respond(c, true)}>
                {t.arena.accept}
              </button>
            </div>
          </div>
        );
      })}
      {error && (
        <div class="toast toast-error" onClick={() => setError(null)}>
          {error}
        </div>
      )}
      {pmNotices.map((n) => (
        <div class="toast pm-toast" onClick={() => arena.dismissPmNotice(n.key)}>
          <strong>✉ {n.from}:</strong> <span class="pm-toast-text">{n.preview}</span>
        </div>
      ))}
      {arena.notices.map((n) => (
        <div class="toast" onClick={() => arena.dismissNotice(n.key)}>
          {noticeText(n.challenge, n.reason)}
        </div>
      ))}
    </div>
  );
}
